import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { 
  ArrowLeft, Heart, Activity, Thermometer, Droplets, Wind,
  AlertTriangle, CheckCircle, Clock, TrendingUp, TrendingDown, Minus,
  Bell, BellOff, Plus, Loader2, Brain, Download, RefreshCw,
  Stethoscope, AlertCircle, ChevronRight, User, Watch, Smartphone,
  Link2, Unlink, Zap, Pill, Package, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function HealthAlerts() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [readings, setReadings] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [showAddReading, setShowAddReading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Wearables state
  const [wearableDashboard, setWearableDashboard] = useState(null);
  const [syncingDevice, setSyncingDevice] = useState(null);
  const [connectingProvider, setConnectingProvider] = useState(null);
  
  // Medications state
  const [medDashboard, setMedDashboard] = useState(null);
  const [showAddMed, setShowAddMed] = useState(false);
  const [savingMed, setSavingMed] = useState(false);
  const [loggingMed, setLoggingMed] = useState(null);
  const [newMed, setNewMed] = useState({
    name: '',
    dosage: '',
    dosage_unit: 'mg',
    frequency: 'once_daily',
    schedule_times: ['08:00'],
    instructions: '',
    prescriber: '',
    doctor_notes: '',
    pharmacy: '',
    quantity_remaining: 30,
    low_stock_threshold: 7,
    push_reminders: true,
    sms_reminders: false
  });
  
  // Form state for adding readings
  const [newReading, setNewReading] = useState({
    metric_type: 'blood_pressure',
    value: {},
    unit: 'fahrenheit',
    notes: ''
  });
  
  useEffect(() => {
    fetchData();
    fetchWearables();
    fetchMedications();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, readingsRes, alertsRes] = await Promise.all([
        api.get(`/health-alerts/dashboard`),
        api.get(`/health-alerts/readings?days=30`),
        api.get(`/health-alerts/alerts/history?days=30`)
      ]);
      
      setDashboard(dashboardRes.data.dashboard);
      setReadings(readingsRes.data.readings || []);
      setAlertHistory(alertsRes.data.alerts || []);
      
      if (dashboardRes.data.dashboard?.profile_id) {
        setProfileId(dashboardRes.data.dashboard.profile_id);
        const settingsRes = await api.get(
          `/health-alerts/settings/${dashboardRes.data.dashboard.profile_id}`,
        );
        setSettings(settingsRes.data.settings);
      }
    } catch (error) {
      console.error('Failed to fetch health data:', error);
      toast.error('Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWearables = async () => {
    try {
      const response = await api.get(`/wearables/dashboard`);
      setWearableDashboard(response.data.dashboard);
    } catch (error) {
      console.error('Failed to fetch wearables:', error);
    }
  };

  const fetchMedications = async () => {
    try {
      const response = await api.get(`/medications/dashboard`);
      setMedDashboard(response.data.dashboard);
    } catch (error) {
      console.error('Failed to fetch medications:', error);
    }
  };

  const handleAddMedication = async () => {
    if (!newMed.name || !newMed.dosage) {
      toast.error('Please enter medication name and dosage');
      return;
    }
    
    setSavingMed(true);
    try {
      await api.post(
        `/medications`,
        { ...newMed, profile_id: profileId },
      );
      toast.success('Medication added successfully');
      setShowAddMed(false);
      setNewMed({
        name: '', dosage: '', dosage_unit: 'mg', frequency: 'once_daily',
        schedule_times: ['08:00'], instructions: '', prescriber: '', doctor_notes: '',
        pharmacy: '', quantity_remaining: 30, low_stock_threshold: 7,
        push_reminders: true, sms_reminders: false
      });
      fetchMedications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add medication');
    } finally {
      setSavingMed(false);
    }
  };

  const handleLogMedication = async (medId, status, scheduledTime) => {
    setLoggingMed(`${medId}_${status}`);
    try {
      await api.post(
        `/medications/${medId}/log`,
        { status, scheduled_time: scheduledTime },
      );
      toast.success(`Medication marked as ${status}`);
      fetchMedications();
    } catch (error) {
      toast.error('Failed to log medication');
    } finally {
      setLoggingMed(null);
    }
  };

  const handleRefillMedication = async (medId, quantity = 30) => {
    try {
      const response = await api.post(
        `/medications/${medId}/refill`,
        { quantity },
      );
      toast.success(response.data.message);
      fetchMedications();
    } catch (error) {
      toast.error('Failed to record refill');
    }
  };

  const handleDeleteMedication = async (medId) => {
    try {
      await api.delete(`/medications/${medId}`);
      toast.success('Medication deleted');
      fetchMedications();
    } catch (error) {
      toast.error('Failed to delete medication');
    }
  };

  const handleConnectWearable = async (provider) => {
    setConnectingProvider(provider);
    try {
      const response = await api.post(
        `/wearables/connect`,
        { provider, profile_id: profileId },
      );
      toast.success(`Connected to ${response.data.message}`);
      fetchWearables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect device');
    } finally {
      setConnectingProvider(null);
    }
  };

  const handleDisconnectWearable = async (connectionId, providerName) => {
    try {
      await api.delete(`/wearables/connections/${connectionId}`);
      toast.success(`Disconnected from ${providerName}`);
      fetchWearables();
    } catch (error) {
      toast.error('Failed to disconnect device');
    }
  };

  const handleSyncWearable = async (connectionId, providerName) => {
    setSyncingDevice(connectionId);
    try {
      const response = await api.post(
        `/wearables/connections/${connectionId}/sync`,
        {},
      );
      toast.success(`Synced ${response.data.readings_synced} readings from ${providerName}`);
      fetchData();
      fetchWearables();
    } catch (error) {
      toast.error('Failed to sync device');
    } finally {
      setSyncingDevice(null);
    }
  };

  const handleAddReading = async () => {
    if (!newReading.metric_type) {
      toast.error('Please select a metric type');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post(
        `/health-alerts/log-reading`,
        {
          profile_id: profileId,
          metric_type: newReading.metric_type,
          value: newReading.value,
          unit: newReading.metric_type === 'temperature' ? newReading.unit : undefined,
          notes: newReading.notes
        },
      );
      
      const { severity, alert_sent } = response.data;
      
      if (severity === 'critical') {
        toast.error('🚨 Critical reading detected! Care Circle has been notified.');
      } else if (severity === 'warning') {
        toast.warning('⚠️ Reading is outside normal range. Please monitor closely.');
      } else {
        toast.success('Reading logged successfully');
      }
      
      setShowAddReading(false);
      setNewReading({ metric_type: 'blood_pressure', value: {}, unit: 'fahrenheit', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAIAnalysis = async () => {
    setAnalyzingAI(true);
    try {
      const response = await api.post(
        `/health-alerts/ai-analysis`,
        { profile_id: profileId },
      );
      
      if (response.data.analysis) {
        setAiAnalysis(response.data.analysis);
        toast.success('AI analysis complete');
      } else {
        toast.info(response.data.message || 'Not enough data for analysis');
      }
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await api.post(
        `/health-alerts/alerts/${alertId}/acknowledge`,
        {},
      );
      toast.success('Alert acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await api.post(
        `/health-alerts/alerts/${alertId}/resolve`,
        { notes: 'Resolved by caregiver' },
      );
      toast.success('Alert resolved');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleUpdateSettings = async (newSettings) => {
    try {
      await api.put(
        `/health-alerts/settings/${profileId}`,
        newSettings,
      );
      setSettings(newSettings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleExportReport = async () => {
    try {
      const response = await api.get(`/health-alerts/export-report?profile_id=${profileId}&days=30`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `health-report-${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const getMetricIcon = (type) => {
    const icons = {
      blood_pressure: Heart,
      heart_rate: Activity,
      temperature: Thermometer,
      blood_oxygen: Wind,
      blood_glucose: Droplets
    };
    return icons[type] || Activity;
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      warning: 'bg-amber-100 text-amber-700 border-amber-200',
      normal: 'bg-green-100 text-green-700 border-green-200'
    };
    return styles[severity] || styles.normal;
  };

  const formatValue = (reading) => {
    const value = reading.value || {};
    if (value.systolic !== undefined) {
      return `${value.systolic}/${value.diastolic} mmHg`;
    }
    return `${value.value || 'N/A'} ${reading.unit || ''}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  // Simple trend chart component
  const TrendChart = ({ data, metricType }) => {
    if (!data || data.length < 2) return <p className="text-slate-400 text-sm">Not enough data</p>;
    
    const values = data.slice(-14).map(d => {
      const v = d.value;
      return v.systolic || v.value || 0;
    });
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    
    return (
      <div className="flex items-end gap-1 h-16">
        {values.map((v, i) => {
          const height = ((v - min) / range) * 100;
          const severity = data[i]?.severity || 'normal';
          const color = severity === 'critical' ? 'bg-red-500' : severity === 'warning' ? 'bg-amber-500' : 'bg-teal-500';
          return (
            <div
              key={i}
              className={`flex-1 ${color} rounded-t transition-all hover:opacity-80`}
              style={{ height: `${Math.max(height, 8)}%` }}
              title={`${v}`}
            />
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Stethoscope className="w-6 h-6 text-red-500" />
                  AI Health Alerts
                </h1>
                <p className="text-xs sm:text-sm text-slate-500">
                  Monitor vitals and get intelligent health alerts
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleExportReport} variant="outline" size="sm" data-testid="export-btn">
                <Download className="w-4 h-4" />
                Export Report
              </Button>
              <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {!dashboard ? (
          <Card>
            <CardContent className="p-12 text-center">
              <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Care Profile Found</h3>
              <p className="text-slate-500 mb-4">
                {user?.role === 'independent_caregiver'
                  ? 'You need to be assigned to a care circle by a family caregiver.'
                  : 'Create a care profile to start tracking health metrics.'}
              </p>
              {user?.role !== 'independent_caregiver' && (
                <Link to="/care-profile/setup">
                  <Button>Create Care Profile</Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Health Score Card */}
            <Card className={`border-l-4 ${
              dashboard.health_score?.status === 'critical' ? 'border-l-red-500' :
              dashboard.health_score?.status === 'warning' ? 'border-l-amber-500' :
              'border-l-green-500'
            }`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                      dashboard.health_score?.status === 'critical' ? 'bg-red-100' :
                      dashboard.health_score?.status === 'warning' ? 'bg-amber-100' :
                      'bg-green-100'
                    }`}>
                      <span className={`text-3xl font-bold ${
                        dashboard.health_score?.status === 'critical' ? 'text-red-600' :
                        dashboard.health_score?.status === 'warning' ? 'text-amber-600' :
                        'text-green-600'
                      }`}>
                        {dashboard.health_score?.score || '--'}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">{dashboard.senior_name}'s Health Score</h2>
                      <p className="text-slate-600">{dashboard.health_score?.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span>{dashboard.health_score?.readings_count || 0} metrics tracked</span>
                        {dashboard.active_alerts > 0 && (
                          <Badge className="bg-red-100 text-red-700">
                            {dashboard.active_alerts} Active Alert{dashboard.active_alerts > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => setShowAddReading(true)} 
                      className="bg-teal-500 hover:bg-teal-600"
                      data-testid="add-reading-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Log Reading
                    </Button>
                    {/* <Button
                      onClick={handleAIAnalysis}
                      variant="outline"
                      disabled={analyzingAI}
                      data-testid="ai-analysis-btn"
                    >
                      {analyzingAI ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Brain className="w-4 h-4 mr-2" />
                      )}
                      AI Analysis
                    </Button> */}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Analysis — disabled */}

            <Tabs defaultValue="dashboard" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4" data-testid="health-tabs">
                <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                <TabsTrigger value="medications">
                  <Pill className="w-4 h-4 mr-1" />
                  Meds
                </TabsTrigger>
                <TabsTrigger value="readings">Readings</TabsTrigger>
                <TabsTrigger value="alerts">Alerts</TabsTrigger>
              </TabsList>

              {/* Dashboard Tab */}
              <TabsContent value="dashboard" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(dashboard.metric_definitions || {}).map(([key, config]) => {
                    const reading = dashboard.latest_readings?.[key];
                    const Icon = getMetricIcon(key);
                    const trendData = dashboard.trend_data?.[key] || [];
                    
                    return (
                      <Card key={key} data-testid={`metric-card-${key}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Icon className="w-5 h-5 text-teal-500" />
                              {config.label}
                            </CardTitle>
                            {reading && (
                              <Badge className={getSeverityBadge(reading.severity)}>
                                {reading.severity}
                              </Badge>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent>
                          {reading ? (
                            <>
                              <p className="text-2xl font-bold text-slate-900 mb-1">
                                {formatValue(reading)}
                              </p>
                              <p className="text-xs text-slate-500 mb-3">
                                {formatDateTime(reading.logged_at)}
                              </p>
                              <TrendChart data={trendData} metricType={key} />
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-slate-400 text-sm">No readings yet</p>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="mt-2"
                                onClick={() => {
                                  setNewReading({ ...newReading, metric_type: key });
                                  setShowAddReading(true);
                                }}
                              >
                                <Plus className="w-4 h-4" />
                                Add Reading
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Medications Tab */}
              <TabsContent value="medications" className="space-y-4">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Pill className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{medDashboard?.total_medications || 0}</p>
                          <p className="text-xs text-slate-500">Medications</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{medDashboard?.pending_today || 0}</p>
                          <p className="text-xs text-slate-500">Pending</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{medDashboard?.taken_today || 0}</p>
                          <p className="text-xs text-slate-500">Taken</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">
                            {medDashboard?.overall_adherence_7_days || '--'}%
                          </p>
                          <p className="text-xs text-slate-500">Adherence</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Low Stock Alerts */}
                {medDashboard?.low_stock_count > 0 && (
                  <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <span className="font-medium text-amber-700">
                          {medDashboard.low_stock_count} medication(s) running low!
                        </span>
                      </div>
                      <div className="mt-3 space-y-2">
                        {medDashboard.low_stock_medications?.map(med => (
                          <div key={med.medication_id} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                            <span className="text-sm text-amber-800">{med.name} - {med.quantity_remaining} left</span>
                            <Button size="sm" variant="outline" onClick={() => handleRefillMedication(med.medication_id)}>
                              <Package className="w-3 h-3" />
                              Refill
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Today's Schedule */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Today's Medication Schedule</CardTitle>
                      <Button onClick={() => setShowAddMed(true)} data-testid="add-med-btn">
                        <Plus className="w-4 h-4" />
                        Add Medication
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {!medDashboard?.todays_schedule?.length ? (
                      <div className="text-center py-8">
                        <Pill className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No medications scheduled today</p>
                        <Button variant="outline" className="mt-3" onClick={() => setShowAddMed(true)}>
                          <Plus className="w-4 h-4" />
                          Add Your First Medication
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {medDashboard.todays_schedule.map((item, idx) => (
                          <div 
                            key={`${item.medication_id}_${item.scheduled_time}_${idx}`}
                            className={`flex items-center justify-between p-4 rounded-lg border ${
                              item.status === 'taken' ? 'bg-green-50 border-green-200' :
                              item.status === 'missed' ? 'bg-red-50 border-red-200' :
                              'bg-slate-50 border-slate-200'
                            }`}
                            data-testid="med-schedule-item"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                item.status === 'taken' ? 'bg-green-100' :
                                item.status === 'missed' ? 'bg-red-100' :
                                'bg-blue-100'
                              }`}>
                                <Pill className={`w-6 h-6 ${
                                  item.status === 'taken' ? 'text-green-600' :
                                  item.status === 'missed' ? 'text-red-600' :
                                  'text-blue-600'
                                }`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-slate-900">{item.name}</p>
                                  {item.low_stock && (
                                    <Badge className="bg-amber-100 text-amber-700 text-xs">Low Stock</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">
                                  {item.dosage} {item.dosage_unit} • {item.scheduled_time}
                                </p>
                                {item.instructions && (
                                  <p className="text-xs text-slate-500">{item.instructions}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {item.status === 'pending' ? (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-green-500 hover:bg-green-600"
                                    onClick={() => handleLogMedication(item.medication_id, 'taken', item.scheduled_time)}
                                    disabled={loggingMed === `${item.medication_id}_taken`}
                                    data-testid="mark-taken-btn"
                                  >
                                    {loggingMed === `${item.medication_id}_taken` ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <CheckCircle className="w-4 h-4" />
                                    )}
                                    Taken
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-slate-500"
                                    onClick={() => handleLogMedication(item.medication_id, 'skipped', item.scheduled_time)}
                                    disabled={loggingMed === `${item.medication_id}_skipped`}
                                  >
                                    Skip
                                  </Button>
                                </>
                              ) : (
                                <Badge className={
                                  item.status === 'taken' ? 'bg-green-100 text-green-700' :
                                  item.status === 'missed' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-600'
                                }>
                                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                </Badge>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                                onClick={() => handleDeleteMedication(item.medication_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* All Medications List */}
                {medDashboard?.medications?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>All Medications</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {medDashboard.medications.map(med => (
                          <div key={med.medication_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900">{med.name}</p>
                              <p className="text-sm text-slate-500">
                                {med.dosage} {med.dosage_unit} • {med.frequency?.replace('_', ' ')}
                              </p>
                              {med.prescriber && <p className="text-xs text-slate-400">Prescriber: {med.prescriber}</p>}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-sm font-medium">
                                  {med.adherence_7_days?.percentage !== null ? `${med.adherence_7_days.percentage}% adherence` : '--'}
                                </p>
                                <p className="text-xs text-slate-500">{med.quantity_remaining || 0} doses left</p>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                                onClick={() => handleDeleteMedication(med.medication_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Readings Tab */}
              <TabsContent value="readings" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Recent Readings</CardTitle>
                      <Button onClick={() => setShowAddReading(true)} size="sm">
                        <Plus className="w-4 h-4" />
                        Add Reading
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {readings.length === 0 ? (
                      <div className="text-center py-8">
                        <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No readings recorded yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {readings.slice(0, 20).map((reading) => {
                          const Icon = getMetricIcon(reading.metric_type);
                          return (
                            <div 
                              key={reading.reading_id} 
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                              data-testid="reading-item"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  reading.severity === 'critical' ? 'bg-red-100' :
                                  reading.severity === 'warning' ? 'bg-amber-100' :
                                  'bg-teal-100'
                                }`}>
                                  <Icon className={`w-5 h-5 ${
                                    reading.severity === 'critical' ? 'text-red-600' :
                                    reading.severity === 'warning' ? 'text-amber-600' :
                                    'text-teal-600'
                                  }`} />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    {dashboard.metric_definitions?.[reading.metric_type]?.label || reading.metric_type}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    {formatDateTime(reading.logged_at)} • {reading.logged_by_name}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-slate-900">{formatValue(reading)}</p>
                                <Badge className={getSeverityBadge(reading.severity)}>
                                  {reading.severity}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Wearables Tab */}
              <TabsContent value="wearables" className="space-y-4">
                {/* Connected Devices */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Watch className="w-5 h-5 text-teal-500" />
                      Connected Devices
                    </CardTitle>
                    <CardDescription>Manage your wearable health devices</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {wearableDashboard?.connections?.length > 0 ? (
                      <div className="space-y-3">
                        {wearableDashboard.connections.map((conn) => (
                          <div key={conn.connection_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid="wearable-connection">
                            <div className="flex items-center gap-4">
                              <div 
                                className="w-12 h-12 rounded-xl flex items-center justify-center"
                                style={{ backgroundColor: conn.provider_info?.color + '20' }}
                              >
                                {conn.provider === 'fitbit' && <Activity className="w-6 h-6" style={{ color: conn.provider_info?.color }} />}
                                {conn.provider === 'apple_health' && <Smartphone className="w-6 h-6" style={{ color: conn.provider_info?.color }} />}
                                {conn.provider === 'google_fit' && <Heart className="w-6 h-6" style={{ color: conn.provider_info?.color }} />}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">{conn.provider_info?.name}</p>
                                <p className="text-sm text-slate-500">
                                  {conn.last_sync ? `Last sync: ${formatDateTime(conn.last_sync)}` : 'Never synced'}
                                </p>
                                <div className="flex items-center gap-1 mt-1">
                                  {conn.demo_mode && <Badge variant="outline" className="text-xs">Demo Mode</Badge>}
                                  {conn.auto_sync && <Badge className="bg-green-100 text-green-700 text-xs">Auto-sync</Badge>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSyncWearable(conn.connection_id, conn.provider_info?.name)}
                                disabled={syncingDevice === conn.connection_id}
                                data-testid="sync-device-btn"
                              >
                                {syncingDevice === conn.connection_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDisconnectWearable(conn.connection_id, conn.provider_info?.name)}
                                data-testid="disconnect-device-btn"
                              >
                                <Unlink className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Watch className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No devices connected</p>
                        <p className="text-sm text-slate-400 mt-1">Connect a wearable to auto-sync health data</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Available Providers */}
                <Card>
                  <CardHeader>
                    <CardTitle>Connect a Device</CardTitle>
                    <CardDescription>Link your wearable to automatically sync health readings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.entries(wearableDashboard?.available_providers || {}).map(([key, provider]) => {
                        const isConnected = wearableDashboard?.connections?.some(c => c.provider === key);
                        
                        return (
                          <div 
                            key={key} 
                            className={`p-4 rounded-lg border-2 transition-all ${
                              isConnected ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-teal-300'
                            }`}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: provider.color + '20' }}
                              >
                                {key === 'fitbit' && <Activity className="w-5 h-5" style={{ color: provider.color }} />}
                                {key === 'apple_health' && <Smartphone className="w-5 h-5" style={{ color: provider.color }} />}
                                {key === 'google_fit' && <Heart className="w-5 h-5" style={{ color: provider.color }} />}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">{provider.name}</p>
                                {isConnected && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Connected</Badge>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-slate-500 mb-3">{provider.description}</p>
                            <div className="flex flex-wrap gap-1 mb-3">
                              {provider.metrics.map(m => (
                                <Badge key={m} variant="outline" className="text-xs">
                                  {m.replace('_', ' ')}
                                </Badge>
                              ))}
                            </div>
                            {!isConnected && (
                              <Button
                                className="w-full"
                                variant="outline"
                                onClick={() => handleConnectWearable(key)}
                                disabled={connectingProvider === key}
                                data-testid={`connect-${key}-btn`}
                              >
                                {connectingProvider === key ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Link2 className="w-4 h-4" />
                                )}
                                Connect
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Sync Stats */}
                {wearableDashboard?.today_readings > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Today's Auto-Synced Readings</p>
                            <p className="text-sm text-slate-500">{wearableDashboard.today_readings} readings from wearables</p>
                          </div>
                        </div>
                        <Badge className="bg-teal-100 text-teal-700">{wearableDashboard.connected_devices} device(s)</Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Alerts Tab */}
              <TabsContent value="alerts" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Alert History</CardTitle>
                    <CardDescription>Health alerts triggered in the last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {alertHistory.length === 0 ? (
                      <div className="text-center py-8">
                        <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No alerts triggered</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {alertHistory.map((alert) => (
                          <div 
                            key={alert.alert_id} 
                            className={`p-4 rounded-lg border-l-4 ${
                              alert.severity === 'critical' ? 'border-l-red-500 bg-red-50' :
                              'border-l-amber-500 bg-amber-50'
                            }`}
                            data-testid="alert-item"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={getSeverityBadge(alert.severity)}>
                                    {alert.severity.toUpperCase()}
                                  </Badge>
                                  <Badge variant="outline">
                                    {alert.status}
                                  </Badge>
                                </div>
                                <p className="font-medium text-slate-900">{alert.message}</p>
                                <p className="text-sm text-slate-500 mt-1">
                                  {formatDateTime(alert.created_at)}
                                </p>
                              </div>
                              {alert.status === 'active' && (
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleAcknowledgeAlert(alert.alert_id)}
                                  >
                                    Acknowledge
                                  </Button>
                                  <Button 
                                    size="sm"
                                    onClick={() => handleResolveAlert(alert.alert_id)}
                                  >
                                    Resolve
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Alert Settings</CardTitle>
                    <CardDescription>Configure how and when you receive health alerts</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <Bell className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Health Alerts Enabled</p>
                          <p className="text-sm text-slate-500">Receive alerts when readings are abnormal</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings?.alerts_enabled ?? true}
                        onCheckedChange={(checked) => handleUpdateSettings({ ...settings, alerts_enabled: checked })}
                        data-testid="alerts-toggle"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Bell className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">SMS Alerts</p>
                          <p className="text-sm text-slate-500">Send SMS for critical alerts</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings?.sms_alerts_enabled ?? true}
                        onCheckedChange={(checked) => handleUpdateSettings({ ...settings, sms_alerts_enabled: checked })}
                        data-testid="sms-toggle"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Bell className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Push Notifications</p>
                          <p className="text-sm text-slate-500">Send push notifications for alerts</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings?.push_alerts_enabled ?? true}
                        onCheckedChange={(checked) => handleUpdateSettings({ ...settings, push_alerts_enabled: checked })}
                        data-testid="push-toggle"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Add Reading Modal */}
            {showAddReading && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md">
                  <CardHeader>
                    <CardTitle>Log Health Reading</CardTitle>
                    <CardDescription>Enter a new health measurement</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Metric Type</Label>
                      <Select 
                        value={newReading.metric_type} 
                        onValueChange={(v) => setNewReading({ ...newReading, metric_type: v, value: {} })}
                      >
                        <SelectTrigger data-testid="metric-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="blood_pressure">Blood Pressure</SelectItem>
                          <SelectItem value="heart_rate">Heart Rate</SelectItem>
                          <SelectItem value="temperature">Temperature</SelectItem>
                          <SelectItem value="blood_oxygen">Blood Oxygen (SpO2)</SelectItem>
                          <SelectItem value="blood_glucose">Blood Glucose</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Blood Pressure Fields */}
                    {newReading.metric_type === 'blood_pressure' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Systolic (mmHg)</Label>
                          <Input
                            type="number"
                            placeholder="120"
                            value={newReading.value.systolic || ''}
                            onChange={(e) => setNewReading({
                              ...newReading,
                              value: { ...newReading.value, systolic: parseInt(e.target.value) || 0 }
                            })}
                            data-testid="systolic-input"
                          />
                        </div>
                        <div>
                          <Label>Diastolic (mmHg)</Label>
                          <Input
                            type="number"
                            placeholder="80"
                            value={newReading.value.diastolic || ''}
                            onChange={(e) => setNewReading({
                              ...newReading,
                              value: { ...newReading.value, diastolic: parseInt(e.target.value) || 0 }
                            })}
                            data-testid="diastolic-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Heart Rate Field */}
                    {newReading.metric_type === 'heart_rate' && (
                      <div>
                        <Label>Heart Rate (BPM)</Label>
                        <Input
                          type="number"
                          placeholder="72"
                          value={newReading.value.value || ''}
                          onChange={(e) => setNewReading({
                            ...newReading,
                            value: { value: parseInt(e.target.value) || 0 }
                          })}
                          data-testid="heart-rate-input"
                        />
                      </div>
                    )}

                    {/* Temperature Field */}
                    {newReading.metric_type === 'temperature' && (
                      <>
                        <div>
                          <Label>Unit</Label>
                          <Select 
                            value={newReading.unit} 
                            onValueChange={(v) => setNewReading({ ...newReading, unit: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                              <SelectItem value="celsius">Celsius (°C)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Temperature ({newReading.unit === 'celsius' ? '°C' : '°F'})</Label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder={newReading.unit === 'celsius' ? '37.0' : '98.6'}
                            value={newReading.value.value || ''}
                            onChange={(e) => setNewReading({
                              ...newReading,
                              value: { value: parseFloat(e.target.value) || 0 }
                            })}
                            data-testid="temperature-input"
                          />
                        </div>
                      </>
                    )}

                    {/* Blood Oxygen Field */}
                    {newReading.metric_type === 'blood_oxygen' && (
                      <div>
                        <Label>SpO2 (%)</Label>
                        <Input
                          type="number"
                          placeholder="98"
                          value={newReading.value.value || ''}
                          onChange={(e) => setNewReading({
                            ...newReading,
                            value: { value: parseInt(e.target.value) || 0 }
                          })}
                          data-testid="spo2-input"
                        />
                      </div>
                    )}

                    {/* Blood Glucose Field */}
                    {newReading.metric_type === 'blood_glucose' && (
                      <div>
                        <Label>Blood Glucose (mg/dL)</Label>
                        <Input
                          type="number"
                          placeholder="100"
                          value={newReading.value.value || ''}
                          onChange={(e) => setNewReading({
                            ...newReading,
                            value: { value: parseInt(e.target.value) || 0 }
                          })}
                          data-testid="glucose-input"
                        />
                      </div>
                    )}

                    <div>
                      <Label>Notes (Optional)</Label>
                      <Textarea
                        placeholder="Any additional notes..."
                        value={newReading.notes}
                        onChange={(e) => setNewReading({ ...newReading, notes: e.target.value })}
                        data-testid="notes-input"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => setShowAddReading(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        className="flex-1 bg-teal-500 hover:bg-teal-600"
                        onClick={handleAddReading}
                        disabled={submitting}
                        data-testid="submit-reading-btn"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Log Reading
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Add Medication Modal */}
            {showAddMed && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                <Card className="w-full max-w-lg my-8">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Pill className="w-5 h-5 text-blue-500" />
                      Add Medication
                    </CardTitle>
                    <CardDescription>Set up medication with reminders</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Medication Name *</Label>
                        <Input
                          placeholder="e.g., Lisinopril"
                          value={newMed.name}
                          onChange={(e) => setNewMed({ ...newMed, name: e.target.value })}
                          data-testid="med-name-input"
                        />
                      </div>
                      <div>
                        <Label>Dosage *</Label>
                        <Input
                          placeholder="e.g., 10"
                          value={newMed.dosage}
                          onChange={(e) => setNewMed({ ...newMed, dosage: e.target.value })}
                          data-testid="med-dosage-input"
                        />
                      </div>
                      <div>
                        <Label>Unit</Label>
                        <Select value={newMed.dosage_unit} onValueChange={(v) => setNewMed({ ...newMed, dosage_unit: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mg">mg</SelectItem>
                            <SelectItem value="ml">ml</SelectItem>
                            <SelectItem value="tablets">tablets</SelectItem>
                            <SelectItem value="capsules">capsules</SelectItem>
                            <SelectItem value="units">units</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Frequency</Label>
                      <Select value={newMed.frequency} onValueChange={(v) => setNewMed({ ...newMed, frequency: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="once_daily">Once Daily</SelectItem>
                          <SelectItem value="twice_daily">Twice Daily</SelectItem>
                          <SelectItem value="three_times_daily">Three Times Daily</SelectItem>
                          <SelectItem value="four_times_daily">Four Times Daily</SelectItem>
                          <SelectItem value="every_other_day">Every Other Day</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="as_needed">As Needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Reminder Time(s)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="time"
                          value={newMed.schedule_times[0] || '08:00'}
                          onChange={(e) => setNewMed({ ...newMed, schedule_times: [e.target.value] })}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Set when to receive reminders</p>
                    </div>

                    <div>
                      <Label>Instructions</Label>
                      <Input
                        placeholder="e.g., Take with food"
                        value={newMed.instructions}
                        onChange={(e) => setNewMed({ ...newMed, instructions: e.target.value })}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prescriber</Label>
                        <Input
                          placeholder="Doctor name"
                          value={newMed.prescriber}
                          onChange={(e) => setNewMed({ ...newMed, prescriber: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Pharmacy</Label>
                        <Input
                          placeholder="Pharmacy name"
                          value={newMed.pharmacy}
                          onChange={(e) => setNewMed({ ...newMed, pharmacy: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Doctor Notes</Label>
                      <Textarea
                        placeholder="Any special instructions from doctor..."
                        value={newMed.doctor_notes}
                        onChange={(e) => setNewMed({ ...newMed, doctor_notes: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Current Stock</Label>
                        <Input
                          type="number"
                          value={newMed.quantity_remaining}
                          onChange={(e) => setNewMed({ ...newMed, quantity_remaining: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <Label>Low Stock Alert At</Label>
                        <Input
                          type="number"
                          value={newMed.low_stock_threshold}
                          onChange={(e) => setNewMed({ ...newMed, low_stock_threshold: parseInt(e.target.value) || 7 })}
                        />
                      </div>
                    </div>

                    <div className="space-y-3 pt-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-slate-500" />
                          <span className="text-sm">Push Notifications</span>
                        </div>
                        <Switch
                          checked={newMed.push_reminders}
                          onCheckedChange={(v) => setNewMed({ ...newMed, push_reminders: v })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-slate-500" />
                          <span className="text-sm">SMS Reminders</span>
                        </div>
                        <Switch
                          checked={newMed.sms_reminders}
                          onCheckedChange={(v) => setNewMed({ ...newMed, sms_reminders: v })}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => setShowAddMed(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        className="flex-1 bg-blue-500 hover:bg-blue-600"
                        onClick={handleAddMedication}
                        disabled={savingMed}
                        data-testid="save-med-btn"
                      >
                        {savingMed ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Add Medication
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
